"use client";

import Image from "next/image";
import { motion, type Variants } from "motion/react";
import { FaInstagram } from "react-icons/fa";
import { Play, Copy, ArrowUpRight, Heart, MessageCircle } from "lucide-react";
import {
    instagramPosts,
    instagramHandle,
    instagramUrl,
} from "@/data/instagram";
import type { InstagramPost } from "@/types/instagram";

/* ── Motion ─────────────────────────────────────────────────────────────── */

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const gridVariants: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};

const tileVariants: Variants = {
    hidden: { opacity: 0, y: 26 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

/* ── Helpers ────────────────────────────────────────────────────────────── */

const compact = (n?: number) =>
    typeof n === "number"
        ? Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n)
        : null;

const shortDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

/* ── Tile ───────────────────────────────────────────────────────────────── */

function PostTile({
    post,
    index,
    featured,
}: {
    post: InstagramPost;
    index: number;
    featured: boolean;
}) {
    const likes = compact(post.likes);
    const comments = compact(post.comments);

    return (
        <motion.a
            variants={tileVariants}
            href={post.permalink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open Instagram post: ${post.caption}`}
            className={`
        group relative block shrink-0 overflow-hidden bg-[#EAE4D8]
        outline-none
        focus-visible:ring-2 focus-visible:ring-[#9C7A46] focus-visible:ring-offset-2
        focus-visible:ring-offset-[#F7F5F0]

        /* mobile: horizontal snap carousel */
        w-[76vw] max-w-[320px] snap-center
        /* sm+: grid cell */
        sm:w-auto sm:max-w-none

        aspect-square
        ${featured ? "lg:col-span-2 lg:row-span-2" : ""}
      `}
        >
            <Image
                src={post.imageSrc}
                alt={post.imageAlt}
                fill
                loading={index < 2 ? "eager" : "lazy"}
                quality={80}
                sizes={
                    featured
                        ? "(max-width: 639px) 76vw, (max-width: 1023px) 33vw, 50vw"
                        : "(max-width: 639px) 76vw, (max-width: 1023px) 33vw, 25vw"
                }
                className="
          transform-gpu object-cover
          transition-transform duration-[900ms] ease-out
          motion-safe:group-hover:scale-[1.06]
        "
            />

            {/* Base vignette — always on, keeps the grid cohesive */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#17160F]/45 via-transparent to-transparent" />

            {/* Hover ink wash */}
            <div
                className="
          pointer-events-none absolute inset-0 bg-[#17160F]/0
          transition-colors duration-500
          motion-safe:group-hover:bg-[#17160F]/35
        "
            />

            {/* Media-type chip */}
            {post.mediaType !== "IMAGE" && (
                <span
                    aria-hidden="true"
                    className="
            absolute right-2.5 top-2.5 grid h-7 w-7 place-items-center
            rounded-full bg-[#F7F5F0]/85 text-[#17160F] backdrop-blur-sm
            sm:right-3 sm:top-3 sm:h-8 sm:w-8
          "
                >
                    {post.mediaType === "VIDEO" ? (
                        <Play size={12} strokeWidth={2} className="translate-x-[1px] fill-current" />
                    ) : (
                        <Copy size={12} strokeWidth={2} />
                    )}
                </span>
            )}

            {/* Corner bracket — echoes the viewfinder motif */}
            <span
                aria-hidden="true"
                className="
          pointer-events-none absolute left-2.5 top-2.5 h-5 w-5
          border-l border-t border-[#F7F5F0]/0
          transition-colors duration-500
          motion-safe:group-hover:border-[#F7F5F0]/70
          sm:left-3 sm:top-3 sm:h-6 sm:w-6
        "
            />

            {/* Caption plate */}
            <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
                <div
                    className="
            translate-y-0 opacity-100
            motion-safe:sm:translate-y-2 motion-safe:sm:opacity-0
            motion-safe:sm:transition-all motion-safe:sm:duration-500 motion-safe:sm:ease-out
            motion-safe:sm:group-hover:translate-y-0 motion-safe:sm:group-hover:opacity-100
          "
                >
                    <div className="mb-1.5 flex items-center gap-3 text-[10px] font-semibold tracking-[0.18em] text-[#F7F5F0]/80">
                        {likes && (
                            <span className="inline-flex items-center gap-1">
                                <Heart size={11} strokeWidth={2} aria-hidden="true" />
                                {likes}
                            </span>
                        )}
                        {comments && (
                            <span className="inline-flex items-center gap-1">
                                <MessageCircle size={11} strokeWidth={2} aria-hidden="true" />
                                {comments}
                            </span>
                        )}
                        <span className="ml-auto text-[#F7F5F0]/55">{shortDate(post.timestamp)}</span>
                    </div>

                    <p
                        className={`
              text-[12px] font-light leading-snug text-[#F7F5F0]
              [display:-webkit-box] [-webkit-box-orient:vertical] overflow-hidden
              ${featured ? "[-webkit-line-clamp:3] lg:text-[14px]" : "[-webkit-line-clamp:2]"}
            `}
                    >
                        {post.caption}
                    </p>
                </div>
            </div>
        </motion.a>
    );
}

/* ── Section ────────────────────────────────────────────────────────────── */

export function InstagramFeed({
    posts = instagramPosts,
    featureFirst = true,
}: {
    posts?: InstagramPost[];
    featureFirst?: boolean;
}) {
    if (!posts.length) return null;

    return (
        <section
            aria-labelledby="instagram-heading"
            className="
        relative w-full overflow-x-clip bg-[#F7F5F0]
        py-16 sm:py-20 lg:py-28
      "
        >
            <div
                className="
          mx-auto max-w-[1360px]
          px-[max(1.25rem,env(safe-area-inset-left))]
          sm:px-10 lg:px-16 xl:px-24
        "
            >
                {/* ── Header ── */}
                <motion.div
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="
            flex flex-col gap-7
            lg:flex-row lg:items-end lg:justify-between lg:gap-16
          "
                >
                    <div className="relative min-w-0 pl-5 sm:pl-8">
                        <span
                            aria-hidden="true"
                            className="absolute bottom-1 left-0 top-1 w-px bg-gradient-to-b from-[#9C7A46] via-[#9C7A46]/30 to-transparent"
                        />

                        <p className="text-[10.5px] font-semibold uppercase tracking-[0.28em] text-[#9C7A46] sm:text-[12px] sm:tracking-[0.4em]">
                            Journal
                            <span className="mx-2 text-[#17160F]/25">—</span>
                            Instagram
                        </p>

                        <h2
                            id="instagram-heading"
                            className="
                mt-4 flex flex-col font-display leading-[0.94] tracking-tight text-[#17160F]
                text-[clamp(2rem,8.5vw,3.2rem)]
                sm:mt-5 sm:text-[clamp(2.6rem,6vw,4rem)]
                lg:mt-6 lg:leading-[0.92] lg:text-[clamp(3rem,4.4vw,4.6rem)]
                [overflow-wrap:anywhere]
              "
                        >
                            <span className="font-light">From the</span>
                            <span className="font-black uppercase tracking-tighter">Workshop</span>
                        </h2>

                        <p className="mt-5 max-w-[46ch] text-[14.5px] font-light leading-[1.7] text-[#5A5548] sm:text-[15px] lg:text-[16px]">
                            Process, patina and finished plate — documented daily from our
                            Moradabad atelier.
                        </p>
                    </div>

                    {/* Follow CTA — ink fill-wipe, matches the product button */}
                    <a
                        href={instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="
              group relative flex w-full shrink-0 items-center justify-center gap-3
              overflow-hidden border border-[#17160F] px-8 py-4
              text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[#17160F]
              min-h-[48px]
              transition-colors duration-300
              sm:w-auto sm:px-10 sm:text-[11px] sm:tracking-[0.3em]
              lg:mb-1
            "
                    >
                        <span
                            aria-hidden="true"
                            className="
                absolute inset-0 translate-y-full bg-[#17160F]
                transition-transform duration-[350ms] ease-[cubic-bezier(0.65,0,0.35,1)]
                motion-safe:group-hover:translate-y-0
              "
                        />
                        <span className="relative z-10 inline-flex items-center gap-2.5 transition-colors duration-300 group-hover:text-[#F7F5F0]">
                            <FaInstagram size={14} strokeWidth={1.8} aria-hidden="true" />
                            @{instagramHandle}
                            <ArrowUpRight
                                size={14}
                                strokeWidth={2}
                                aria-hidden="true"
                                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                            />
                        </span>
                    </a>
                </motion.div>

                {/* Hairline */}
                <div className="mt-10 h-px w-full bg-[#17160F]/10 sm:mt-12 lg:mt-16" />
            </div>

            {/* ── Feed ──
          < 640px  → edge-to-edge horizontal snap carousel (no cramped 2-col grid)
          ≥ 640px  → 3-col grid
          ≥ 1024px → 4-col bento, first tile 2×2
      */}
            <motion.div
                variants={gridVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="
          mt-8 flex gap-3
          snap-x snap-mandatory overflow-x-auto overscroll-x-contain
          [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
          px-[max(1.25rem,env(safe-area-inset-left))]
          pb-2

          sm:mt-12 sm:grid sm:grid-cols-3 sm:gap-3 sm:overflow-visible sm:px-10 sm:pb-0
          lg:mt-16 lg:grid-cols-4 lg:gap-4 lg:px-16
          xl:px-24

          sm:mx-auto sm:max-w-[1360px]
        "
            >
                {posts.map((post, i) => (
                    <PostTile
                        key={post.id}
                        post={post}
                        index={i}
                        featured={featureFirst && i === 0}
                    />
                ))}

                {/* Right-edge spacer so the last card can snap clear of the viewport edge */}
                <span aria-hidden="true" className="w-2 shrink-0 sm:hidden" />
            </motion.div>

            {/* Mobile swipe affordance */}
            <p className="mt-4 px-5 text-center text-[10px] uppercase tracking-[0.28em] text-[#17160F]/35 sm:hidden">
                Swipe to explore
            </p>
        </section>
    );
}

export default InstagramFeed;